import "./multi_uploader.scss";
import { Component } from "react";
import PropTypes from "prop-types";
import { Subject, takeUntil } from "rxjs";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import Button from "common/core/button";
import { BASE_ACCEPTED } from "util/uploader";
import AlertMessage from "common/core/alert_message";
import { QUEUED, UPLOADING, SUCCESS } from "common/document/uploader/add_modal/upload_view";
import { MAX_FILE_SIZE_BYTES } from "constants/document_bundle";
import { WEB_DOC_SCANNING } from "constants/feature_gates";
import { useFeatureFlag } from "common/feature_gating";
import { isMobileDevice } from "util/support";
import WorkflowModalV2 from "common/modals/workflow_modal/v2";
import { Paragraph } from "common/core/typography";
import SROnly from "common/core/screen_reader";
import { useScreenClassInfo } from "common/core/responsive";

import { DocScanner } from "../doc_scanner";
import { ModalFocusedElementScreenReaderWrapper, DocScannerButton } from "../doc_scanner/common";
import MultiUploadRow from "./row";
import DropzoneMultiUploader from "./dropzone_multi_uploader";

const CX_NAME = "MultiUploader";

const FILE_NUM_LIMIT = 20;

const messages = defineMessages({
  uploadError: {
    id: "4857c60f-11e1-4b14-8a00-daf4f1798cba",
    defaultMessage: "There was an error uploading the document. Please try again.",
  },
  fileSizeError: {
    id: "1cc1b46a-179a-41cf-b6f8-c818228bced3",
    defaultMessage: "Oops! Documents that exceed the 30MB size limit could not be uploaded.",
  },
  noFilesError: {
    id: "bed51434-24e5-4d9a-b04c-2a920a52c460",
    defaultMessage: "Could not find any files of acceptable filetype. PDF files are preferred.",
  },
  fileNumberError: {
    id: "db6e1ab1-88c1-4c06-874b-587c1934f597",
    defaultMessage: `Oops! You can only upload up to ${FILE_NUM_LIMIT} documents.
      Upload documents with multiple pages as a single .pdf`,
  },
  documentUploading: {
    id: "c3a759a1-720f-4378-bdb1-f7aca7fa82a8",
    defaultMessage: "Document is uploading",
  },
  documentUploaded: {
    id: "de22ca5b-19ca-4427-9d25-2356e293d7a9",
    defaultMessage: "Document uploaded",
  },
});

const defaultValidateSelectedFiles = (selectedFiles, uploadedFiles) => {
  if (selectedFiles.length === 0) {
    return { acceptedFiles: selectedFiles, fileUploadError: messages.noFilesError };
  }

  if (uploadedFiles.length + selectedFiles.length > FILE_NUM_LIMIT) {
    return { acceptedFiles: [], fileUploadError: messages.fileNumberError };
  }

  const acceptedFiles = selectedFiles.filter((file) => {
    return file.size <= MAX_FILE_SIZE_BYTES;
  });

  const fileUploadError = acceptedFiles.length !== selectedFiles.length && messages.fileSizeError;

  return { acceptedFiles, fileUploadError };
};

const preventFilePreviewLoading = function (event) {
  event.preventDefault(); // In case the user misses the drop zone
};

class MultiUploader extends Component {
  state = {
    uploadedDocuments: [],
    fileUploadError: null,
    completeLoading: false,
    showDocScanner: false,
    openDocumentUploadModal: false,
  };

  componentDidMount() {
    window.addEventListener("dragover", preventFilePreviewLoading);
    window.addEventListener("drop", preventFilePreviewLoading);

    this.unmounted$ = new Subject();
    this.props.uploadedDocuments$.pipe(takeUntil(this.unmounted$)).subscribe({
      next: (uploadedDocumentsUpdaterFn) => {
        this.setState((prevState) => ({
          uploadedDocuments: uploadedDocumentsUpdaterFn(prevState.uploadedDocuments),
        }));
      },
    });
  }

  componentWillUnmount() {
    window.removeEventListener("dragover", preventFilePreviewLoading);
    window.removeEventListener("drop", preventFilePreviewLoading);

    const { unmounted$ } = this;

    unmounted$.next();
    unmounted$.complete();
    this.unmounted$ = null;
  }

  triggerFileSelect() {
    this.inputElement.click();
  }

  handleFileOnChange = ({ target }) => {
    const files = Array.from(target.files || []);
    if (files.length) {
      this.uploadAdditionalFiles(files);
      // In Chrome, selecting the same file twice in a row does not change the `<input />`
      // so the onChange does not fire. To combat this, we just imediately reset the value.
      target.value = "";
    }
  };

  handleStopRenameDocument = (document, newName) => {
    if (newName) {
      const { name } = document;
      const lastIndex = name.lastIndexOf(".");
      const fileExtension = lastIndex > 0 ? name.slice(lastIndex) : "";
      const validName = `${newName}${fileExtension}`;
      this.setState((prevState) => ({
        uploadedDocuments: this.renameDocument(document, validName, prevState),
      }));
    }
  };

  handleComplete = async () => {
    this.setState({ completeLoading: true });
    const { uploadedDocuments } = this.state;
    const completeDocuments = uploadedDocuments
      .filter((doc) => doc.status === SUCCESS)
      .map((doc, index) => ({
        id: doc.userDocument.id,
        name: doc.name,
        bundlePosition: index,
      }));
    try {
      const result = this.props.completeStrategy(completeDocuments);
      if (result instanceof Promise) {
        await result;
        this.setState({ completeLoading: false });
      }
    } catch (err) {
      this.setState({ fileUploadError: messages.unexpected_error, completeLoading: false });
    }
  };

  renameDocument(document, validName, prevState) {
    const { id } = document;
    const uploadedDocuments = [...prevState.uploadedDocuments];
    const documentIndex = uploadedDocuments.map((doc) => doc.id).indexOf(id);
    const newDocument = { ...uploadedDocuments[documentIndex] };
    newDocument.name = validName;
    uploadedDocuments[documentIndex] = newDocument;
    return uploadedDocuments;
  }

  uploadAdditionalFiles = (selectedFiles) => {
    const validationFn =
      typeof this.props.validateSelectedFiles === "function"
        ? this.props.validateSelectedFiles
        : this.defaultValidateSelectedFiles;
    const { acceptedFiles, fileUploadError } = validationFn(
      selectedFiles,
      this.state.uploadedDocuments,
    );

    this.setState({ fileUploadError });
    this.props.onSelectFiles(acceptedFiles);
    this.closeDocumentUploadModal();
  };

  closeDocumentUploadModal = () => {
    this.setState({ openDocumentUploadModal: false });
  };

  render() {
    const {
      onDeleteDocument,
      intl,
      inMeeting,
      isSmall,
      onCancel,
      webDocScanningEnabled,
      enableScanning,
    } = this.props;
    const {
      uploadedDocuments,
      fileUploadError,
      completeLoading,
      showDocScanner,
      openDocumentUploadModal,
    } = this.state;
    const hasDocuments = Boolean(uploadedDocuments.length);
    const isLoading = uploadedDocuments.some(
      (doc) => doc.status === UPLOADING || doc.status === QUEUED,
    );
    const hasUploadedDoc = uploadedDocuments.some((doc) => doc.status === SUCCESS);
    const acceptedFileFormats = Object.keys(BASE_ACCEPTED).join(",");
    const continueButtonAutomationId = "continue-to-document-button";
    const addMoreAutomationId = "add-document-button";
    const mWebScanningEnabled = isMobileDevice() && enableScanning && webDocScanningEnabled;
    const uploadBlob = (data, name) => {
      const file = new File([data], name, { type: "application/pdf" });
      this.uploadAdditionalFiles([file]);
    };

    if (showDocScanner) {
      return (
        <DocScanner
          onClose={() => this.setState({ showDocScanner: false })}
          onUploadDocument={uploadBlob}
        />
      );
    }

    const dropzoneUploader = () => (
      <DropzoneMultiUploader
        onDrop={this.uploadAdditionalFiles}
        accept={BASE_ACCEPTED}
        showTos={false}
        onSelectUploadType={
          mWebScanningEnabled
            ? () => {
                this.setState({ openDocumentUploadModal: true });
              }
            : undefined
        }
      />
    );

    const content = () => {
      return hasDocuments ? (
        <div className="MultiRowView">
          <SROnly>
            <div role="status" aria-busy={isLoading}>
              {isLoading
                ? intl.formatMessage(messages.documentUploading)
                : intl.formatMessage(messages.documentUploaded)}
            </div>
          </SROnly>
          <div className="MultiRows">
            <ul>
              {uploadedDocuments.map((document, index) => (
                <MultiUploadRow
                  documentIndex={index}
                  key={document.id}
                  document={document}
                  onDeleteDocument={onDeleteDocument}
                  onStopRenameDocument={this.handleStopRenameDocument}
                />
              ))}
            </ul>
            <Button
              className="AddDocumentButton"
              variant={isSmall ? "secondary" : "tertiary"}
              buttonSize={isSmall ? "large" : "condensed"}
              buttonColor="action"
              fullwidth
              onClick={
                mWebScanningEnabled
                  ? () => this.setState({ openDocumentUploadModal: true })
                  : this.triggerFileSelect.bind(this)
              }
              automationId={addMoreAutomationId}
            >
              <FormattedMessage
                id="3cc02d77-79e1-4a00-92bf-c0359e4c438e"
                defaultMessage="<span>+</span>Add another document"
                values={{
                  span: (chunks) => <span aria-hidden="true">{chunks}&nbsp;</span>,
                }}
              />
            </Button>
          </div>
          <Button
            className="ContinueToDocumentButton"
            buttonSize="large"
            buttonColor="action"
            variant="primary"
            fullwidth={isSmall ? true : false}
            onClick={this.handleComplete}
            disabled={isLoading || !hasUploadedDoc}
            isLoading={completeLoading}
            data-automation-id={continueButtonAutomationId}
            automationId={continueButtonAutomationId}
          >
            {isLoading ? (
              <FormattedMessage
                id="95a7c4aa-5c12-4fcd-9f0a-752d126f0ef1"
                defaultMessage="Uploading..."
              />
            ) : inMeeting ? (
              <FormattedMessage
                id="7a30d831-3b4b-4357-88b1-923eafd11b7f"
                defaultMessage="Upload and continue"
              />
            ) : (
              <FormattedMessage
                id="24bf62fd-5c9f-4782-b73f-def18ab2fe33"
                defaultMessage="Continue to document"
              />
            )}
          </Button>
        </div>
      ) : (
        dropzoneUploader()
      );
    };

    const documentContent = () => {
      return inMeeting ? (
        <WorkflowModalV2
          footerSeparator={false}
          buttons={[
            <Button
              key="cancel"
              buttonSize="large"
              buttonColor="action"
              variant="tertiary"
              fullwidth
              onClick={onCancel}
            >
              <FormattedMessage id="a308e670-a508-45ac-b28b-f9438cff2127" defaultMessage="Cancel" />
            </Button>,
          ]}
        >
          {content()}
        </WorkflowModalV2>
      ) : (
        content()
      );
    };

    return (
      <>
        {openDocumentUploadModal && (
          <WorkflowModalV2
            autoFocus
            className="AddDocumentModal"
            closeBehavior={{
              tag: "without-button",
              onClose: () => this.closeDocumentUploadModal,
            }}
            footerSeparator={false}
            title={
              <ModalFocusedElementScreenReaderWrapper>
                <FormattedMessage
                  id="a80ca8d1-3521-44a9-a458-963834581349"
                  defaultMessage="How do you want to add your document?"
                />
              </ModalFocusedElementScreenReaderWrapper>
            }
            buttons={[
              <DocScannerButton
                key="upload"
                variant="secondary"
                buttonSize="large"
                buttonColor="action"
                fullwidth
                withIcon={{ name: "upload", placement: "left" }}
                onClick={this.triggerFileSelect.bind(this)}
              >
                <FormattedMessage
                  id="8286e94e-94b4-403d-9812-3f957cf66ca9"
                  defaultMessage="Upload a document"
                />
              </DocScannerButton>,
              <DocScannerButton
                key="scan"
                variant="secondary"
                buttonSize="large"
                buttonColor="action"
                fullwidth
                withIcon={{ name: "camera", placement: "left" }}
                onClick={() =>
                  this.setState({ showDocScanner: true, openDocumentUploadModal: false })
                }
              >
                <FormattedMessage
                  id="5833d7c2-8a72-4105-b08c-2229aa755552"
                  defaultMessage="Scan with camera"
                />
              </DocScannerButton>,
              <Button
                buttonSize="large"
                buttonColor="action"
                variant="tertiary"
                fullwidth
                key="cancel"
                onClick={() => this.closeDocumentUploadModal()}
              >
                <FormattedMessage
                  id="abbd0f89-57e8-4493-864c-ad3401921c21"
                  defaultMessage="Cancel"
                />
              </Button>,
            ]}
          >
            <Paragraph size="large">
              <FormattedMessage
                id="6c04bfa1-a9e2-4d38-9ef6-f3df5ab9a268"
                defaultMessage="You can upload a digital document, or scan a paper document with your camera."
              />
            </Paragraph>
          </WorkflowModalV2>
        )}
        <div className={CX_NAME} data-automation-id="uploader">
          {fileUploadError && (
            <AlertMessage className="UploaderAlertBanner" kind="danger">
              {intl.formatMessage(fileUploadError)}
            </AlertMessage>
          )}
          {documentContent()}
          <input
            type="file"
            id="MultiDocumentUploadFileSelect"
            ref={(ref) => {
              this.inputElement = ref;
            }}
            onChange={this.handleFileOnChange}
            accept={acceptedFileFormats}
            multiple
            hidden
          />
        </div>
      </>
    );
  }
}

MultiUploader.propTypes = {
  // From DocumentUploadHandler
  onDeleteDocument: PropTypes.func.isRequired,
  // From DocumentUploadHandler
  onSelectFiles: PropTypes.func.isRequired,
  // Upload Observable
  uploadedDocuments$: PropTypes.object.isRequired,
  // Validations from parent component
  validateSelectedFiles: PropTypes.func.isRequired,
  // To finalize bundle
  completeStrategy: PropTypes.func.isRequired,
  enableScanning: PropTypes.bool,
  intl: PropTypes.object,
  // mweb scanning LD flag
  webDocScanningEnabled: PropTypes.bool,
  inMeeting: PropTypes.bool,
};

MultiUploader.defaultProps = {
  validateSelectedFiles: defaultValidateSelectedFiles,
};

function MultiUploaderContainer(props) {
  const intl = useIntl();
  const { isSmall } = useScreenClassInfo();
  const webDocScanningEnabled = useFeatureFlag(WEB_DOC_SCANNING);
  return (
    <MultiUploader
      {...props}
      intl={intl}
      webDocScanningEnabled={webDocScanningEnabled}
      isSmall={isSmall}
    />
  );
}

export default MultiUploaderContainer;
